import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Play, Pause, Square, CheckCircle2, Loader2, Plus, Clock, Wrench, User,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError, clock, dateOf, fmtDuration, getToken, isLeader } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PhotoGrid from "@/components/PhotoGrid";
import InvoicePanel from "@/components/InvoicePanel";
import LineItems, { sumItems } from "@/components/LineItems";
import { TimeEntriesPanel } from "@/components/TimeEntriesPanel";
import StatusChangeButton from "@/components/StatusChangeButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const L = ({ children }) => (
  <Label className="text-xs font-medium uppercase tracking-[0.05em] text-slate-500">{children}</Label>
);

const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const hours = (seconds) => `${(seconds / 3600).toFixed(1).replace(".", ",")} h`;

export default function Installation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, company } = useAuth();
  const [job, setJob] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const [finishOpen, setFinishOpen] = useState(false);
  const [manual, setManual] = useState({ date: todayLocal(), hours: "" });
  const [staff, setStaff] = useState([]);
  const [note, setNote] = useState("");
  const fileRef = useRef(null);
  const imageRef = useRef(null);
  const docRef = useRef(null);
  const token = getToken();
  const readOnly = !!company?.demo_readonly;
  const invoiced = !!job?.invoiced;

  const saveItems = async (key, items) => {
    setJob((j) => ({ ...j, [key]: items }));
    try {
      await api.put(`/jobs/${id}/items`, { [key]: items });
    } catch (e) {
      toast.error(apiError(e));
      load();
    }
  };
  const leader = isLeader(user?.role);

  const load = useCallback(async () => {
    try {
      const [{ data: j }, { data: s }] = await Promise.all([
        api.get(`/jobs/${id}`),
        api.get(`/jobs/${id}/installation/summary`),
      ]);
      setJob(j);
      setSummary(s);
    } catch (e) {
      toast.error(apiError(e));
      navigate("/jobb");
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/users").then(({ data }) => setStaff(data.filter((u) => u.active !== false))).catch(() => {});
  }, []);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const post = async (path, ok) => {
    setBusy(true);
    try {
      await api.post(`/jobs/${id}/${path}`);
      toast.success(ok);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const act = async (path, ok) => {
    setBusy(true);
    try {
      await api.post(`/jobs/${id}/installation/${path}`);
      toast.success(ok);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const addManual = async () => {
    setBusy(true);
    try {
      await api.post(`/jobs/${id}/installation/manual-time`, {
        date: manual.date,
        hours: parseFloat(String(manual.hours).replace(",", ".")),
      });
      toast.success("Tid tillagd");
      setManual({ date: todayLocal(), hours: "" });
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.post(`/jobs/${id}/notes`, { text: note.trim(), phase: "during" });
      setNote("");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (e, kind) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post(`/jobs/${id}/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uppladdat");
      await load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const removePhoto = async (photoId) => {
    try {
      await api.delete(`/jobs/${id}/photos/${photoId}`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeFile = async (fileId) => {
    try {
      await api.delete(`/jobs/${id}/files/${fileId}`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!job || !summary) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  const finished = summary.status === "finished";
  const mySession = (summary.sessions || []).find((s) => s.user_id === user?.id);
  const running = !!mySession?.running;
  const paused = !!mySession && !mySession.running;
  const elapsed = mySession
    ? mySession.elapsed_seconds + (running ? Math.floor((Date.now() - new Date(mySession.running_since).getTime()) / 1000) : 0)
    : 0;
  const orderNo = job.order_number ? String(job.order_number).padStart(4, "0") : "–";

  return (
    <div className="mx-auto w-full max-w-3xl xl:max-w-5xl" data-testid="installation-page">
      <Button
        variant="ghost"
        data-testid="installation-back"
        onClick={() => navigate("/jobb")}
        className="mb-2 h-9 px-2 text-slate-600 hover:bg-slate-100"
      >
        <ArrowLeft className="mr-1.5 h-4 w-4" /> Tillbaka
      </Button>

      <div className="rounded-lg border border-purple-200 bg-white p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded bg-purple-700 px-2 py-1 font-mono text-xs font-bold text-white" data-testid="installation-order-number">
            #{orderNo}
          </span>
          <span className="rounded-md border border-purple-200 bg-purple-50 px-2 py-1 text-xs font-semibold uppercase tracking-[0.05em] text-purple-800" data-testid="installation-type-badge">
            Nymontering
          </span>
          <span
            className={`rounded-md border px-2 py-1 text-xs font-semibold uppercase tracking-[0.05em] ${
              finished ? "border-green-200 bg-green-50 text-green-700" : "border-blue-200 bg-blue-50 text-blue-700"
            }`}
            data-testid="installation-status"
          >
            {finished ? "Avslutad" : "Aktiv"}
          </span>
          <h1 className="w-full min-w-0 text-2xl font-bold text-slate-900 sm:w-auto sm:flex-1">{job.title}</h1>
          {leader && <StatusChangeButton job={job} onSaved={load} />}
        </div>
        <div className="mt-2 grid grid-cols-1 gap-1 text-sm text-slate-700 sm:grid-cols-2">
          <p data-testid="installation-machine-no"><Wrench className="mr-1.5 inline h-4 w-4 text-slate-400" />Maskinnr: <b>{job.machine_no || "–"}</b></p>
          <p data-testid="installation-customer"><User className="mr-1.5 inline h-4 w-4 text-slate-400" />Kund: <b>{job.customer_name || "–"}</b></p>
        </div>
        {job.description && <p className="mt-2 whitespace-pre-wrap text-sm text-slate-600">{job.description}</p>}
        {finished && (
          <p className="mt-2 text-sm font-semibold text-green-800" data-testid="installation-finished-info">
            Avslutad {dateOf(job.install_finished_at)} · {job.install_finished_by}
          </p>
        )}
      </div>

      {/* Tidsflöde: START / PAUS / STOP / AVSLUT */}
      {!finished && !readOnly && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-white p-4" data-testid="installation-timer-panel">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-xl font-semibold text-slate-800">Tidsregistrering</h2>
            <span className="font-mono text-2xl font-bold text-slate-900" data-testid="installation-elapsed">
              {clock(elapsed)}
            </span>
          </div>
          <p className="mt-1 text-sm text-slate-600">
            {running ? "Tidtagning pågår" : paused ? "Pausad – tryck START för att fortsätta" : "Tryck START för att börja registrera tid"}
          </p>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Button
              data-testid="installation-start"
              disabled={busy || running}
              onClick={() => act("start", running ? "" : "Tidtagning startad")}
              className="h-14 bg-green-600 font-heading text-base font-bold text-white hover:bg-green-700"
            >
              <Play className="mr-1.5 h-5 w-5" /> START
            </Button>
            <Button
              data-testid="installation-pause"
              disabled={busy || !running}
              onClick={() => act("pause", "Pausad")}
              className="h-14 bg-amber-500 font-heading text-base font-bold text-white hover:bg-amber-600"
            >
              <Pause className="mr-1.5 h-5 w-5" /> PAUS
            </Button>
            <Button
              data-testid="installation-stop"
              disabled={busy || !mySession}
              onClick={() => act("stop", "Dagens tid sparad")}
              className="h-14 bg-slate-800 font-heading text-base font-bold text-white hover:bg-slate-900"
            >
              <Square className="mr-1.5 h-5 w-5" /> STOP
            </Button>
            <Button
              data-testid="installation-finish"
              disabled={busy}
              onClick={() => setFinishOpen(true)}
              className="h-14 bg-purple-700 font-heading text-base font-bold text-white hover:bg-purple-800"
            >
              <CheckCircle2 className="mr-1.5 h-5 w-5" /> AVSLUT
            </Button>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            STOP sparar dagens arbetstid – ordern förblir aktiv och kan fortsättas nästa dag. AVSLUT stänger hela nymonteringen.
          </p>
        </div>
      )}

      {/* Tid per dag och total */}
      <div className="mt-3 rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-xl font-semibold text-slate-800">Arbetstid</h2>
          <span className="text-base font-bold text-slate-900 md:text-lg" data-testid="installation-total">
            Total arbetstid: {hours(summary.total_seconds)}
          </span>
        </div>

        <div className="mt-3 space-y-1" data-testid="installation-day-list">
          {summary.per_day.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="installation-no-time">Ingen tid registrerad ännu</p>
          ) : (
            summary.per_day.map((d) => (
              <div key={d.date} className="flex items-center justify-between rounded border border-slate-100 bg-slate-50 px-3 py-2 text-sm" data-testid={`installation-day-${d.date}`}>
                <span className="font-medium text-slate-800">{d.date}</span>
                <span className="font-mono font-semibold text-slate-900">{hours(d.seconds)}</span>
              </div>
            ))
          )}
        </div>

        {summary.entries.length > 0 && (
          <div className="mt-3 space-y-1" data-testid="installation-entry-list">
            <L>Poster</L>
            {summary.entries.map((e) => (
              <div key={e.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 py-1.5 text-sm" data-testid={`installation-entry-${e.id}`}>
                <span className="text-slate-700">
                  {e.date} · {e.user_name}
                  {e.source === "manual" && (
                    <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800" data-testid={`installation-entry-manual-${e.id}`}>
                      Manuell
                    </span>
                  )}
                </span>
                <span className="font-mono text-slate-900">{fmtDuration(e.seconds)}</span>
              </div>
            ))}
          </div>
        )}

        {summary.per_user.length > 0 && (
          <div className="mt-3" data-testid="installation-per-user">
            <L>Per person</L>
            {summary.per_user.map((u) => (
              <div key={u.name} className="flex justify-between py-1 text-sm text-slate-700">
                <span>{u.name}</span>
                <span className="font-mono">{hours(u.seconds)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Manuell tidsregistrering – tekniker, datum, tider, redigera och ta bort */}
      {company?.features?.manual_time && (
        <TimeEntriesPanel job={job} staff={staff} onSaved={load} readOnly={readOnly} />
      )}

      {/* Material, reservdelar och annat – tilldelad tekniker får fylla i själv */}
      <div className="mt-3 space-y-3 rounded-lg border border-slate-200 bg-white p-4" data-testid="installation-items">
        <h2 className="text-xl font-semibold text-slate-800">Material och delar</h2>
        <LineItems
          label="Material"
          testid="installation-materials"
          category="material"
          readOnly={readOnly || invoiced}
          items={job.materials}
          onChange={(v) => saveItems("materials", v)}
        />
        <LineItems
          label="Reservdelar"
          testid="installation-spare-parts"
          category="spare_part"
          readOnly={readOnly || invoiced}
          items={job.spare_parts}
          onChange={(v) => saveItems("spare_parts", v)}
        />
        <LineItems
          label="Annat"
          testid="installation-others"
          category="other"
          readOnly={readOnly || invoiced}
          items={job.others}
          onChange={(v) => saveItems("others", v)}
        />
        <p className="text-right text-sm font-semibold text-slate-800" data-testid="installation-items-total">
          Summa{" "}
          {(sumItems(job.materials) + sumItems(job.spare_parts) + sumItems(job.others)).toFixed(2)} kr
        </p>
      </div>

      {/* Bilder och filer */}
      <div className="mt-3">
        <PhotoGrid
          photos={job.photos || []}
          files={job.files || []}
          jobId={id}
          token={token}
          busy={busy}
          fileRef={fileRef}
          imageRef={imageRef}
          docRef={docRef}
          onFile={(e) => upload(e, "photos")}
          onImage={(e) => upload(e, "photos")}
          onDoc={(e) => upload(e, "files")}
          onAdd={() => fileRef.current?.click()}
          onDelete={removePhoto}
          onDocDelete={removeFile}
          readOnly={readOnly}
        />
      </div>

      {/* Fritext */}
      <div className="mt-3 rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="text-xl font-semibold text-slate-800">Fritext / anteckningar</h2>
        <div className="mt-2 space-y-1" data-testid="installation-notes">
          {(job.notes || []).length === 0 ? (
            <p className="text-sm text-slate-500">Inga anteckningar</p>
          ) : (
            job.notes.map((n) => (
              <div key={n.id} className="rounded border border-slate-100 bg-slate-50 px-3 py-2 text-sm" data-testid={`installation-note-${n.id}`}>
                <p className="whitespace-pre-wrap text-slate-800">{n.text}</p>
                <p className="mt-0.5 text-xs text-slate-500">{n.user_name} · {n.created_at?.slice(0, 16).replace("T", " ")}</p>
              </div>
            ))
          )}
        </div>
        {!readOnly && (
          <div className="mt-2 space-y-2">
            <Textarea
              data-testid="installation-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Skriv en anteckning…"
            />
            <Button
              data-testid="installation-note-add"
              disabled={busy || !note.trim()}
              onClick={addNote}
              className="h-10 bg-blue-600 text-white hover:bg-blue-700"
            >
              <Plus className="mr-1.5 h-4 w-4" /> Spara anteckning
            </Button>
          </div>
        )}
      </div>

      {/* Fakturering – samma flöde och endpoints som vanliga ordrar */}
      {finished && (
        <div className="mt-3 space-y-3" data-testid="installation-invoice-section">
          <InvoicePanel jobId={id} />
          {job.invoiced ? (
            <div className="rounded-lg border border-indigo-300 bg-indigo-50 p-4 text-sm font-semibold text-indigo-900" data-testid="installation-invoiced-badge">
              Fakturerad: {dateOf(job.invoiced_at)} {job.invoiced_by ? `· ${job.invoiced_by}` : ""}
            </div>
          ) : job.ready_to_invoice ? (
            leader && !readOnly && (
              <Button
                data-testid="installation-invoiced"
                disabled={busy}
                onClick={() => post("invoiced", "Markerad som fakturerad")}
                className="h-14 w-full bg-indigo-700 font-heading text-base font-bold text-white hover:bg-indigo-800"
              >
                FAKTURERAD
              </Button>
            )
          ) : (
            !readOnly && (
              <Button
                data-testid="installation-ready-to-invoice"
                disabled={busy}
                onClick={() => post("ready-to-invoice", "Färdig för fakturering")}
                className="h-14 w-full bg-teal-700 font-heading text-base font-bold text-white hover:bg-teal-800"
              >
                FÄRDIG FÖR FAKTURERING
              </Button>
            )
          )}
        </div>
      )}

      <div className="mt-3 rounded-lg border border-slate-900 bg-slate-900 p-4 text-white" data-testid="installation-grand-total">
        <h2 className="text-xs font-medium uppercase tracking-[0.05em] text-slate-300">
          Totalt på nymonteringen (inkl. manuellt registrerad tid)
        </h2>
        <div className="mt-2 flex items-center justify-between border-t border-slate-700 pt-2">
          <span className="text-sm font-semibold uppercase tracking-[0.05em]">Total arbetad tid</span>
          <span className="font-heading text-2xl font-bold" data-testid="installation-grand-total-time">
            {fmtDuration(summary.total_seconds)}
          </span>
        </div>
      </div>

      <p className="mt-4 flex items-center gap-1.5 text-xs text-slate-500">
        <Clock className="h-3.5 w-3.5" /> Nymontering är ett verkstads-/monteringsarbete – inga rese-, kart- eller körsträckefunktioner.
      </p>

      <AlertDialog open={finishOpen} onOpenChange={setFinishOpen}>
        <AlertDialogContent className="bg-white" data-testid="installation-finish-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Är hela nymonteringen färdig?</AlertDialogTitle>
            <AlertDialogDescription>
              Pågående tid sparas först. Ordern markeras som avslutad och hamnar bland avslutade ordrar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="installation-finish-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction
              data-testid="installation-finish-ok"
              disabled={busy}
              onClick={(e) => { e.preventDefault(); setFinishOpen(false); act("finish", "Nymonteringen är avslutad"); }}
              className="bg-purple-700 text-white hover:bg-purple-800"
            >
              Ja, avsluta nymonteringen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
